import ButtonT from '@components/tailwind-components/Button/ButtonT';
import DropDownSearchInput from '@components/tailwind-components/Dropdown/DropDownSearchInput';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import ColorInputWithLabelT from '@components/tailwind-components/Input/ColorInputWithLabelT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import NumberInputWithLabelT from '@components/tailwind-components/Input/NumberInputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import FileUploadT from '@components/tailwind-components/Upload/FileUploadT';
import { TIMEZONES } from '@conversed/shared/src/central/constants/timezones';
import { getVoiceUILanguageOptions } from '@conversed/shared/src/central/constants/voice-ai.config';
import type { WebclientConfig } from '@conversed/shared/src/central/dto/webclient-config.dto';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import { FeatureType } from '@conversed/shared/src/central/types/feature-access.type';
import {
  AnimationType,
  DEFAULT_CHAT_WINDOW_FONT_FAMILY,
  getDefaultValuesForWebclient,
  IconType,
  type TWebclientAvailability,
} from '@conversed/shared/src/central/types/webclient-config.type';
import { colorConfigs } from '@conversed/shared/src/central/types/webclient-style.type';
import { FONT_DEFINITIONS } from '@conversed/shared/src/helpers/font.helpers';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFileMedia } from '@hooks/useFileMedia';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import clsx from 'clsx';
import type React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { convertDataFilesToBase64Format } from '../../../../helpers/fileHelper';
import { useModal } from '../../../../hooks/modal/useModal';
import type { WebclientFormData } from '../../../../interfaces/IWebclient';
import { TimetableInput } from '../../../tailwind-components/Input/TimetableInput';
import { WebclientMenu } from './menu/WebclientMenu';
import WebclientInputGroup from './WebclientInputGroup';

interface Props {
  data: WebclientConfig;
  name: string;
  onSubmit: (data: WebclientFormData) => void;
}

export const WebclientDataModal: React.FC<Props> = ({
  data,
  name,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    designer: { revisions },
    voiceAi: { agents: voiceAIAgents },
  } = useMainSelector();
  const { hasWebclientVoice } = usePlatformFeatures();

  const revisionSelection = useMemo(
    () =>
      revisions?.map((revision) => {
        return {
          value: revision._id,
          description: revision.name,
        };
      }) ?? [],
    [revisions],
  );

  const voiceAIAgentSelection = useMemo(
    () =>
      voiceAIAgents.map((agent) => ({
        value: agent.agentId,
        description: agent.agentName,
      })),
    [voiceAIAgents],
  );

  const voiceUILanguageSelection = useMemo(
    () => getVoiceUILanguageOptions(t),
    [t],
  );

  // TODO: Add better way to config defaults
  const defaultValues = getDefaultValuesForWebclient(data);

  const { handleSubmit, control, watch, getValues, setValue } = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      name,
      ...{
        ...defaultValues,
      },
    },
  });

  const availabilityType: TWebclientAvailability['type'] = watch(
    'options.availability.type',
  );
  const defaultWindowStates = [
    { value: 'greeting', description: 'Greeting' },
    { value: 'fullscreen', description: 'Fullscreen' },
    { value: 'open', description: 'Open' },
    { value: 'closed', description: 'Closed' },
  ];

  const windowType = watch('options.defaultState') || 'open';
  const selectedRevisionId = watch('options.revisionId') || '';

  const { hideModal } = useModal();

  const hideAndSubmit = async (data: typeof defaultValues) => {
    const convertedData = await convertDataFilesToBase64Format(data);
    onSubmit({
      name: convertedData.name,
      config: {
        ...{
          ...convertedData,
          options: convertedData.options,
          scheme: {
            ...convertedData.scheme,
            message: {
              ...convertedData.scheme.message,
              urlTextDecoration: convertedData.scheme.message.urlTextDecoration
                ? 'underline'
                : 'none',
            },
          },
          name: undefined,
        },
      },
    });
    hideModal();
  };

  const { getImageByURL } = useFileMedia();

  const handleFileUpload = (file: TFile | string, field: string) => {
    if (typeof file !== 'string' && 'base64' in file) {
      // Set the file object to the form value
      setValue(field as any, file);
    } else if (file === '') {
      // Handle file removal
      setValue(field as any, undefined);
    }
  };

  const closedIconType = watch('options.icons.chatIcon.closed.type');

  const { hasFeatureAccess } = useFeatureAccess();
  const hasAnimationAccess = hasFeatureAccess(FeatureType.CHAT_AVATAR_TYPE);
  const hasFontSelectionAccess = hasFeatureAccess(FeatureType.CHAT_WINDOW_FONT);

  const fontFamilies = useMemo(
    () =>
      FONT_DEFINITIONS.map((font) => ({
        ...font,
        // The dropdown previews each option in its own font via this field
        fontFamily: font.value,
      })),
    [],
  );

  const chatWindowFont =
    watch('options.fonts.chatWindow') || DEFAULT_CHAT_WINDOW_FONT_FAMILY;

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.name')}>
          <InputWithLabelT
            type='text'
            field='name'
            placeholder={t('settings.webclient.config.form.config.name')}
            label={t('settings.webclient.config.form.config.name')}
            id='name'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.revision')}>
          <SelectInputWithLabelT
            field={'options.revisionId'}
            label={t('settings.webclient.config.form.config.revision.type')}
            id={'webclientRevisions'}
            control={control}
            values={revisionSelection}
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.texts')}>
          <InputWithLabelT
            type='text'
            field='options.title'
            placeholder={t('settings.webclient.config.form.config.title')}
            label={t('settings.webclient.config.form.config.title')}
            id='title'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />

          <InputWithLabelT
            type='text'
            field='options.subtitle'
            placeholder={t('settings.webclient.config.form.config.subtitle')}
            label={t('settings.webclient.config.form.config.subtitle')}
            id='subtitle'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />

          <InputWithLabelT
            type='text'
            field='options.welcomeMessage'
            placeholder={t(
              'settings.webclient.config.form.config.welcome-message',
            )}
            label={t('settings.webclient.config.form.config.welcome-message')}
            id='welcomeMessage'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />

          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.show-welcome-message',
            )}
            control={control}
            field={'options.showWelcomeAsFirstMessage'}
            id='showWelcomeAsFirstMessage'
          />

          <InputWithLabelT
            type='text'
            field='options.messagePlaceholder'
            placeholder={t('settings.webclient.config.form.config.placeholder')}
            label={t('settings.webclient.config.form.config.placeholder')}
            id='messagePlaceholder'
            control={control}
          />

          <InputWithLabelT
            type='text'
            field='options.greeting.buttonText'
            placeholder={t(
              'settings.webclient.config.form.config.greeting-button',
            )}
            label={t('settings.webclient.config.form.config.greeting-button')}
            id='greetingButtonText'
            control={control}
          />

          <InputWithLabelT
            type='text'
            field='options.waitingForTheBotResponseMessage'
            placeholder={t(
              'settings.webclient.config.form.config.waiting-for-the-bot-response-message',
            )}
            label={t(
              'settings.webclient.config.form.config.waiting-for-the-bot-response-message',
            )}
            id='waitingForTheBotResponseMessage'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.enable-waiting-for-the-bot-response-blocker',
            )}
            control={control}
            field={'options.enableWaitingForBotResponseBlocker'}
            id='enableWaitingForBotResponseBlocker'
          />

          <InputWithLabelT
            type='text'
            field='options.notificationAIGeneratedAnswer'
            placeholder={t(
              'settings.webclient.config.form.config.ai-generated-answer',
            )}
            label={t(
              'settings.webclient.config.form.config.ai-generated-answer',
            )}
            id='notificationAIGeneratedAnswer'
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.options')}>
          <InputWithLabelT
            type='text'
            field='options.refParam'
            placeholder={t('settings.webclient.config.form.config.ref-param')}
            label={t('settings.webclient.config.form.config.ref-param')}
            id='refParam'
            control={control}
          />

          <WebclientMenu
            name='options.menuTemplate'
            watch={watch}
            control={control}
            setValue={setValue}
            selectedRevisionId={selectedRevisionId}
          />

          <div className='mt-3'> </div>
          <InputWithLabelT
            type='text'
            field='options.language'
            placeholder={t('settings.webclient.config.form.config.language')}
            label={t('settings.webclient.config.form.config.language')}
            id='language'
            control={control}
          />

          <SelectInputWithLabelT
            field='options.defaultState'
            label={t('settings.webclient.config.form.config.window-state')}
            id='defaultState'
            control={control}
            values={defaultWindowStates}
          />

          {windowType === 'greeting' && (
            <CheckboxInputT
              label={t(
                'settings.webclient.config.form.config.allow-later-button',
              )}
              control={control}
              field={'options.greeting.allowLaterButton'}
              id='greetingAllowLaterButton'
            />
          )}

          <SelectInputWithLabelT
            field='options.icons.logo.type'
            label={t('settings.webclient.config.form.config.logo-type')}
            id='iconLogoType'
            control={control}
            values={[
              { value: 'circle', description: 'Circle' },
              { value: 'square', description: 'Square' },
            ]}
          />

          <SelectInputWithLabelT
            field='options.headerType'
            label={t('settings.webclient.config.form.config.header-type')}
            id='headerType'
            control={control}
            values={[
              { value: 'slim', description: 'Slim' },
              { value: 'normal', description: 'Normal' },
              { value: 'noimage', description: 'No Image' },
            ]}
          />

          <NumberInputWithLabelT
            field='options.greetingTimeout'
            label={t('settings.webclient.config.form.config.greeting-timeout')}
            id='greetingTimeout'
            control={control}
          />

          <NumberInputWithLabelT
            field='options.popupTimeout'
            label={t('settings.webclient.config.form.config.popup-timeout')}
            id='popupTimeout'
            control={control}
          />

          <CheckboxInputT
            label={t('settings.webclient.config.form.config.show-close-button')}
            control={control}
            field={'options.showCloseButton'}
            id='showCloseButton'
          />

          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.enable-attachments',
            )}
            control={control}
            field={'options.useAttachment'}
            id='useAttachment'
          />

          <CheckboxInputT
            label={t('settings.webclient.config.form.config.enable-sound')}
            field={'options.isSoundEnabled'}
            control={control}
            id='isSoundEnabled'
          />

          <CheckboxInputT
            label={t(
              'settings.webclient.config.form.config.require-microsoft-login',
            )}
            control={control}
            field={'options.requireMicrosoftLogin'}
            id='requireMicrosoftLogin'
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.blocker')}>
          <InputWithLabelT
            type='text'
            field='options.blocker.text'
            placeholder={t(
              'settings.webclient.config.form.config.blocker-text',
            )}
            label={t('settings.webclient.config.form.config.blocker-text')}
            id='blockerText'
            control={control}
          />

          <InputWithLabelT
            type='text'
            field='options.blocker.buttonText'
            placeholder={t(
              'settings.webclient.config.form.config.blocker-button',
            )}
            label={t('settings.webclient.config.form.config.blocker-button')}
            id='blockerButtonText'
            control={control}
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.availability')}>
          <SelectInputWithLabelT
            field='options.availability.type'
            label={t('settings.webclient.config.form.config.availability-type')}
            id='availabilityType'
            control={control}
            values={[
              { value: 'always', description: 'Always' },
              { value: 'workprogram', description: 'Work Program' },
            ]}
          />

          {availabilityType === 'workprogram' && (
            <>
              <SelectInputWithLabelT
                label={t('settings.webclient.config.form.config.timezone')}
                field='options.availability.timezone'
                values={TIMEZONES}
                control={control}
              />
              <TimetableInput
                label={t(
                  'settings.webclient.config.form.config.availability-work-program',
                )}
                name='options.availability.workProgram'
                watch={watch}
                control={control}
              />
            </>
          )}
        </WebclientInputGroup>

        {/* The voice section is only visible when the WebclientVoice platform feature flag is enabled for the company */}
        {hasWebclientVoice && (
          <WebclientInputGroup
            title={t('settings.webclient.config.form.group.voice')}>
            <CheckboxInputT
              label={t('settings.webclient.config.form.config.voice-enabled')}
              control={control}
              field={'options.voice.enabled'}
              id='voiceEnabled'
            />
            <SelectInputWithLabelT
              field='options.voice.elevenLabsAgentId'
              label={t('settings.webclient.config.form.config.voice-agent')}
              id='voiceAgentId'
              control={control}
              values={voiceAIAgentSelection}
            />
            <SelectInputWithLabelT
              field='options.voice.language'
              label={t('settings.webclient.config.form.config.voice-language')}
              id='voiceLanguage'
              control={control}
              values={voiceUILanguageSelection}
            />
          </WebclientInputGroup>
        )}

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.icons')}>
          <div className='grid grid-cols-1 gap-1'>
            <div className='flex items-center gap-2'>
              <div className='flex flex-col min-w-[140px] w-[140px]'>
                <LabelT
                  text={t(
                    'settings.webclient.config.form.config.chat-icon-open',
                  )}
                  className='text-sm'
                />
                <span className='text-pico text-gray-400 mb-2'>
                  {t(
                    'settings.webclient.config.form.config.chat-icon-open.desc',
                  )}
                </span>
              </div>
              <FileUploadT
                align='row'
                defaultValue={getImageByURL(
                  defaultValues.options.icons.chatIcon.open,
                )}
                accept={['image/*']}
                disableFromUrl={false}
                onFileUpload={(e) =>
                  handleFileUpload(e, 'options.icons.chatIcon.open')
                }
              />
            </div>

            <div className='tailwind-divider-x opacity-45' />

            <div className='flex items-center gap-2'>
              <div className='flex flex-col min-w-[140px] w-[140px]'>
                <LabelT
                  text={t(
                    'settings.webclient.config.form.config.chat-icon-closed',
                  )}
                  className='text-sm w-[140px]'
                />
                <span className='text-pico text-gray-400 mb-2'>
                  {t(
                    'settings.webclient.config.form.config.chat-icon-closed.desc',
                  )}
                </span>

                <div className='flex flex-col items-start justify-center'>
                  <div className='flex items-center justify-between gap-2'>
                    <div
                      className={clsx(
                        'flex items-center gap-1 text-xs font-medium transition-colors cursor-pointer',
                        closedIconType === IconType.STATIC
                          ? 'text-orange-base'
                          : 'text-gray-500',
                      )}
                      onClick={() => {
                        setValue(
                          'options.icons.chatIcon.closed.type',
                          IconType.STATIC,
                        );
                      }}>
                      <span className='font-medium'>
                        {t(
                          'settings.webclient.config.form.config.icon-type.static',
                        )}
                      </span>
                    </div>

                    <CheckboxTogglerT
                      id='icon-type-toggle'
                      toggleSize='base'
                      colorScheme='orangeGreen'
                      checked={closedIconType === IconType.ANIMATION}
                      disabled={!hasAnimationAccess}
                      onChange={() => {
                        if (closedIconType === IconType.ANIMATION) {
                          // Switching from ANIMATION to STATIC
                          setValue(
                            'options.icons.chatIcon.closed.type',
                            IconType.STATIC,
                          );
                        } else if (hasAnimationAccess) {
                          // Switching from STATIC to ANIMATION — only allowed with the right package
                          setValue(
                            'options.icons.chatIcon.closed.type',
                            IconType.ANIMATION,
                          );
                          // Automatically select the first animation value
                          const firstAnimationType =
                            Object.values(AnimationType)[0];
                          setValue(
                            'options.icons.chatIcon.closed.animation',
                            firstAnimationType,
                          );
                        }
                      }}
                    />

                    <div
                      className={clsx(
                        'flex items-center gap-1 text-xs font-medium transition-colors',
                        hasAnimationAccess
                          ? 'cursor-pointer'
                          : 'cursor-default opacity-50',
                        closedIconType === IconType.ANIMATION &&
                          hasAnimationAccess
                          ? 'text-success'
                          : 'text-gray-500',
                      )}
                      onClick={() => {
                        if (hasAnimationAccess) {
                          setValue(
                            'options.icons.chatIcon.closed.type',
                            IconType.ANIMATION,
                          );
                        }
                      }}>
                      <span className='font-medium'>
                        {t(
                          'settings.webclient.config.form.config.icon-type.animation',
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Show animation selector only if access is available and animation is selected */}
                  {hasAnimationAccess &&
                    closedIconType === IconType.ANIMATION && (
                      <SelectInputWithLabelT
                        labelClassName='text-xs mt-2'
                        className='text-xs leading-5 h-auto'
                        label={t(
                          'settings.webclient.config.form.config.animation-type',
                        )}
                        field='options.icons.chatIcon.closed.animation'
                        values={Object.values(AnimationType).map((value) => ({
                          value,
                          description:
                            value.charAt(0).toUpperCase() + value.slice(1),
                        }))}
                        control={control}
                        selectInputParentClassName='!mb-0'
                      />
                    )}

                  {/* Show upgrade message if no animation access */}
                  {!hasAnimationAccess && (
                    <div className='mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-tiny'>
                      <span className='text-amber-800'>
                        {t(
                          'settings.webclient.config.form.config.icon-type.animation.upgrade-message',
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className='flex flex-col gap-2'>
                <FileUploadT
                  align='row'
                  defaultValue={getImageByURL(
                    defaultValues.options.icons.chatIcon.closed.path,
                  )}
                  accept={['image/*']}
                  disableFromUrl={false}
                  onFileUpload={(e) =>
                    handleFileUpload(e, 'options.icons.chatIcon.closed.path')
                  }
                />
              </div>
            </div>

            <div className='tailwind-divider-x opacity-45' />

            <div className='flex items-center gap-2'>
              <div className='flex flex-col min-w-[140px] w-[140px]'>
                <LabelT
                  text={t('settings.webclient.config.form.config.logo')}
                  className='text-sm w-[140px]'
                />
                <span className='text-pico text-gray-400 mb-2'>
                  {t('settings.webclient.config.form.config.logo.desc')}
                </span>
              </div>
              <FileUploadT
                align='row'
                defaultValue={getImageByURL(
                  defaultValues.options.icons.logo.path,
                )}
                accept={['image/*']}
                disableFromUrl={false}
                onFileUpload={(e) =>
                  handleFileUpload(e, 'options.icons.logo.path')
                }
              />
            </div>

            <div className='tailwind-divider-x opacity-45' />

            <div className='flex items-center gap-2'>
              <div className='flex flex-col min-w-[140px] w-[140px]'>
                <LabelT
                  text={t('settings.webclient.config.form.config.speech-icon')}
                  className='text-sm w-[140px]'
                />
                <span className='text-pico text-gray-400 mb-2'>
                  {t('settings.webclient.config.form.config.speech-icon.desc')}
                </span>
              </div>
              <FileUploadT
                align='row'
                defaultValue={getImageByURL(
                  defaultValues.options.icons.speechIcon,
                )}
                accept={['image/*']}
                disableFromUrl={false}
                onFileUpload={(e) =>
                  handleFileUpload(e, 'options.icons.speechIcon')
                }
              />
            </div>

            <div className='tailwind-divider-x opacity-45' />

            <div className='flex items-center gap-2'>
              <div className='flex flex-col min-w-[140px] w-[140px]'>
                <LabelT
                  text={t(
                    'settings.webclient.config.form.config.greeting-icon',
                  )}
                  className='text-sm w-[140px]'
                />
                <span className='text-pico text-gray-400 mb-2'>
                  {t(
                    'settings.webclient.config.form.config.greeting-icon.desc',
                  )}
                </span>
              </div>
              <FileUploadT
                align='row'
                defaultValue={getImageByURL(
                  defaultValues.options.greeting.messageIcon,
                )}
                accept={['image/*']}
                disableFromUrl={false}
                onFileUpload={(e) =>
                  handleFileUpload(e, 'options.greeting.messageIcon')
                }
              />
            </div>
          </div>
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.fonts')}>
          <DropDownSearchInput
            name='options.fonts.chatWindow'
            label={t(
              'settings.webclient.config.form.config.chat-window-font-type',
            )}
            values={fontFamilies}
            setValue={setValue}
            defaultInputValue={
              fontFamilies.find((font) => font.value === chatWindowFont)
                ?.description || 'Arial (Default)'
            }
            placeholder={t('edit-appearance.fonts.placeholder')}
            labelClassName='text-xs font-medium'
            inputClassName='text-xs leading-5 h-auto'
            dropdownListClassName='!max-h-[160px]'
            dropdownItemClassName='font-preview'
            isEnabled={hasFontSelectionAccess}
          />

          {!hasFontSelectionAccess && (
            <div className='mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-tiny'>
              <span className='text-amber-800'>
                {t(
                  'settings.webclient.config.form.config.chat-window-font-type.upgrade-message',
                )}
              </span>
            </div>
          )}
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.header-colors')}>
          {colorConfigs.headerColors.map(
            ({ fieldName, labelKey, id }, _index) => (
              <ColorInputWithLabelT
                key={id}
                field={fieldName}
                defaultColor={getValues(fieldName)}
                label={t(labelKey)}
                id={id}
                control={control}
                mainClasses='justify-between mb-1'
              />
            ),
          )}
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.message-colors')}>
          {colorConfigs.messageColors.map(
            ({ fieldName, labelKey, id }, _index) => (
              <ColorInputWithLabelT
                key={id}
                field={fieldName}
                defaultColor={getValues(fieldName)}
                label={t(labelKey)}
                id={id}
                control={control}
                mainClasses='justify-between mb-1'
              />
            ),
          )}

          <CheckboxInputT
            label={t('settings.webclient.config.form.config.url-underline')}
            field={'scheme.message.urlTextDecoration'}
            control={control}
            id='urlTextDecoration'
          />
        </WebclientInputGroup>

        <WebclientInputGroup
          title={t('settings.webclient.config.form.group.greeting-colors')}
          className='mb-0'>
          {colorConfigs.greetingColors.map(
            ({ fieldName, labelKey, id }, _index) => (
              <ColorInputWithLabelT
                key={id}
                field={fieldName}
                defaultColor={getValues(fieldName)}
                label={t(labelKey)}
                id={id}
                control={control}
                mainClasses='justify-between mb-1'
              />
            ),
          )}
        </WebclientInputGroup>
      </div>
      <div className='tailwind-modal-footer pr-0'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default WebclientDataModal;
